import { useRef } from "react";
import { capitalize } from "@sellar-io/common";
import { PaymentElement } from "@stripe/react-stripe-js";
import { twMerge } from "tailwind-merge";
import type { CardPaymentOptionsProps } from "../../stockList/checkoutV2/lib/CardPaymentOptions";
import visa from "../../../images/visa.svg";
import mastercard from "../../../images/mastercard.svg";
import amex from "../../../images/amex.svg";

export const UnifiedCheckoutCardPaymentOptions = ({
  existingCardPaymentMethods,
  activePayment,
  allowNewCardPayment,
  onSelectPaymentMethod,
}: CardPaymentOptionsProps) => {
  const lastSavedCard = useRef<string | undefined>(undefined);
  const isNew = activePayment.type === "new-card";
  const selectSavedCard = (paymentMethodId: string) => {
    lastSavedCard.current = paymentMethodId;
    onSelectPaymentMethod({ type: "existing-card", paymentMethodId });
  };
  return (
    <div className="flex flex-col gap-2">
      {!isNew && (
        <div
          role="radiogroup"
          aria-label="Saved payment cards"
          className="flex flex-col gap-2"
        >
          {existingCardPaymentMethods.map((card) => {
            const selected =
              activePayment.paymentMethodId === card.stripePaymentMethodId;
            const logo =
              card.brand.toLowerCase() === "amex"
                ? amex
                : card.brand.toLowerCase() === "mastercard"
                  ? mastercard
                  : visa;
            return (
              <label
                key={card.stripePaymentMethodId}
                className={twMerge(
                  "flex cursor-pointer flex-wrap items-center gap-2 rounded-lg border border-zinc-200 p-4 transition-shadow hover:shadow-md",
                  selected && "border-[#D1A7E9] bg-[#FAF0FF]",
                )}
              >
                <input
                  type="radio"
                  name="unified-saved-card"
                  checked={selected}
                  onChange={() => selectSavedCard(card.stripePaymentMethodId)}
                  className="h-4 w-4 cursor-pointer accent-purple-600"
                />
                <img src={logo} alt="" className="h-5 w-8 object-contain" />
                <span>{capitalize(card.brand)}</span>
                <span className="text-zinc-500">
                  •••• •••• •••• {card.last4Digits}
                </span>
              </label>
            );
          })}
        </div>
      )}
      {allowNewCardPayment && existingCardPaymentMethods.length > 0 && (
        <button
          type="button"
          className="cursor-pointer self-start py-1 text-left font-semibold text-purple-700"
          onClick={() => {
            if (isNew)
              selectSavedCard(
                existingCardPaymentMethods.find(
                  (card) =>
                    card.stripePaymentMethodId === lastSavedCard.current,
                )?.stripePaymentMethodId ??
                  existingCardPaymentMethods[0]!.stripePaymentMethodId,
              );
            else {
              lastSavedCard.current = activePayment.paymentMethodId;
              onSelectPaymentMethod({ type: "new-card" });
            }
          }}
        >
          {isNew
            ? "‹ Back to saved payment card(s)"
            : "Use a different payment card ›"}
        </button>
      )}
      {isNew && allowNewCardPayment && (
        <PaymentElement
          options={{ layout: "tabs", paymentMethodOrder: ["card"] }}
        />
      )}
    </div>
  );
};
